import React from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Card, CardContent, CardHeader, CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BasicInfoForm from './BasicInfoForm';
import SecurityInfoForm from './SecurityInfoForm';
import ElementDialog, { formClasses } from '../ElementDialog';
import userUtils from '../../../services/userUtils';
import { User } from '../../../data/models/user.model';
import * as userSlice from '../../../data/user.slice';
import * as referenceSlice from '../../../data/reference.slice';

interface UserDialogProps {
  open: boolean;
  providerId: number;
  user?: User;
  readOnly: boolean;
  close: (saved: boolean) => void;
}

export default function UserDialog({
  open,
  providerId,
  user,
  readOnly,
  close,
}: UserDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const saving = useAppSelector(userSlice.selectSavingUser);
  const saved = useAppSelector(userSlice.selectSavedUser);
  const savingError = useAppSelector(userSlice.selectSavingError);

  const { control, formState, handleSubmit, reset, watch } = useForm();

  const [title, setTitle] = React.useState('');

  const loadingUser = useAppSelector(userSlice.selectLoadingUser);
  const currentUser = useAppSelector(userSlice.selectUserInfo);
  const userRoles = useAppSelector(userSlice.selectUserRoles);
  const loadingUserRoles = useAppSelector(userSlice.selectLoadingUserRoles);
  const userTypes = useAppSelector(referenceSlice.selectUserTypes);
  const applicationTitles = useAppSelector(
    referenceSlice.selectApplicationTitles,
  );

  const isCurrentUser = user?.userAccountID === currentUser.userAccountID;

  React.useEffect(() => {
    if (open) {
      const defaults: any = {
        ...userUtils.getDefaults(user, userRoles),
      };
      reset(defaults);
    }
  }, [open, user]);

  React.useEffect(() => {
    const typeId = userUtils.getTypeId(userTypes);
    if (typeId) {
      dispatch(userSlice.fetchUserRolesAsync(typeId));
    }
  }, [userTypes, dispatch]);

  React.useEffect(() => {
    if (readOnly) {
      setTitle(t('users.view') || '');
    } else {
      setTitle(
        user?.userAccountID ? t('users.edit') || '' : t('users.create') || '',
      );
    }
  }, [user, readOnly, t]);

  const saveUser = React.useCallback(
    (data: any) => {
      const id = user?.userAccountID || undefined;
      const typeId = userUtils.getTypeId(userTypes);
      const appId = userUtils.getApplicationId(applicationTitles);
      const u = userUtils.getUserFromFormData(id, providerId, data, typeId);
      const fn = id
        ? userSlice.updateUserAsync(u)
        : userSlice.createUserAsync(u);
      dispatch(fn);
    },
    [dispatch, user],
  );

  return (
    <ElementDialog
      title={title}
      faqContext="users_details_form"
      open={open}
      saving={saving}
      saved={saved}
      savingError={savingError}
      save={handleSubmit(saveUser)}
      reset={reset}
      close={close}
      formState={formState}>
      {loadingUser && <CircularProgress />}
      {!loadingUser && userTypes?.length && (
        <Card className={formClasses.card}>
          <CardHeader title={t('users.basicInfo')} />
          <CardContent className={formClasses.cardContent}>
            <BasicInfoForm
              control={control}
              formState={formState}
              requiredFieldMessage={requiredFieldMessage}
              readOnly={readOnly}
              disableActiveCheckbox={!user}
              isCurrentUser={isCurrentUser}
            />
          </CardContent>
        </Card>
      )}
      {!loadingUser && (
        <Card className={formClasses.card}>
          <CardHeader title={t('users.security')} />
          <CardContent className={formClasses.cardContent}>
            <SecurityInfoForm
              user={user}
              control={control}
              userRoles={userRoles}
              loadingUserRoles={loadingUserRoles}
              readOnly={readOnly}
            />
          </CardContent>
        </Card>
      )}
    </ElementDialog>
  );
}
